import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saleAPI } from '../services/api';
import * as repo from '../sync/repo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import { fmtDate } from '../utils/date';
import MoneyInput from '../components/MoneyInput';
import BackButton from '../components/BackButton';
import db from '../db/localdb';
import { toCents, centsToString } from '../utils/money';

// Today's date on this device as YYYY-MM-DD.
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function Debtors() {
  const { business } = useAuth();
  const { toast }    = useToast();
  const navigate     = useNavigate();
  const [debtors,  setDebtors]   = useState([]);
  const [loading,  setLoading]   = useState(true);
  const [payingId, setPayingId]  = useState(null);
  const [payAmt,   setPayAmt]    = useState('');

  const load = () => saleAPI.debtors()
    .then(r => setDebtors(r.data.debtors || []))
    .catch(async (err) => {
      if (err.response) return;
      // No connection: work the list out from the on-device copy, the same
      // way the server does (unpaid, not voided, earliest due date first).
      const bid = business?.id;
      if (!bid) return;
      const today = localToday();
      const rows = (await db.sales.where('business_id').equals(bid).filter(s => !s.voided_at).toArray())
        .map(s => ({ ...s, outstandingCents: toCents(s.total_amount) - toCents(s.amount_paid) }))
        .filter(s => s.outstandingCents > BigInt(0))
        .map(s => {
          const due = s.due_date ? String(s.due_date).slice(0, 10) : null;
          return { ...s, due_date: due, outstanding: centsToString(s.outstandingCents), is_overdue: !!due && due < today };
        });
      rows.sort((a, b) => (a.due_date ? 0 : 1) - (b.due_date ? 0 : 1) || String(a.due_date).localeCompare(String(b.due_date)));
      setDebtors(rows);
    })
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      const result = await repo.addPayment(payingId, { amount: payAmt, paid_at: new Date().toISOString() });
      toast(result._offline ? 'Payment queued (offline)' : 'Payment recorded', 'success');
      setPayingId(null); setPayAmt('');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const fmt = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Debtors</h1>

      {debtors.length === 0 && <p className="text-muted">No outstanding debts.</p>}

      {debtors.map(d => (
        <div key={d.id} className="card" style={{
          marginBottom: '0.85rem',
          boxShadow: '0 6px 16px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
          border: '1px solid var(--color-border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div
              onClick={() => d.customer_id && navigate(`/customers/${d.customer_id}`)}
              style={{ cursor: d.customer_id ? 'pointer' : 'default', flex: 1 }}
            >
              <div style={{ fontWeight: 600 }}>
                {d.buyer_name}
                {d.customer_id && <span className="text-muted" style={{ fontSize: '0.72rem', fontWeight: 400, marginLeft: '0.4rem' }}>View history ›</span>}
              </div>
              {d.buyer_contact && <div className="text-muted">{d.buyer_contact}</div>}
              <div style={{ fontSize: '0.85rem', marginTop: '0.3rem' }}>
                Outstanding: <strong style={{ color: d.is_overdue ? 'var(--color-danger)' : 'var(--color-warning)' }}>{business?.currency} {fmt(d.outstanding)}</strong>
                {d.is_overdue && <span style={{ color: 'var(--color-danger)', marginLeft: '0.5rem', fontSize: '0.75rem' }}>OVERDUE</span>}
              </div>
              {d.due_date && <div className="text-muted" style={{ fontSize: '0.8rem' }}>Due: {fmtDate(d.due_date)}</div>}
            </div>
            <button className="btn-primary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }} onClick={() => { setPayingId(d.id); setPayAmt(''); }}>
              Pay
            </button>
          </div>

          {payingId === d.id && (
            <form onSubmit={handlePayment} style={{ marginTop: '0.75rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
              <div className="form-group">
                <label>Amount received ({business?.currency})</label>
                <MoneyInput value={payAmt} onChange={e => setPayAmt(e.target.value)} required autoFocus />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn-primary">Record</button>
                <button type="button" className="btn-ghost" onClick={() => setPayingId(null)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      ))}
    </div>
  );
}
